import type { components } from "@contracts/generated/editor-service";

/**
 * Preview Insights — the run page's typed state model (#1098).
 *
 * `GET /v1/projects/{projectId}/preview-runs/{previewRunRef}` answers two
 * things side by side: the Editor Service's own run header (`run`, fully typed
 * in the contract) and Brain's `clinical-preview-trace.v1` envelope without its
 * events (`summary`, proxied as JSON because this repository deliberately does
 * NOT vendor Brain's contract — restating it here would be the hand-copied
 * contract the approved design's §8.3 forbids). This module is where that JSON
 * is read into a shape the page can render, and where the two halves are
 * folded into ONE view a clinician can act on.
 *
 * Three rules, all from devops-infrastructure ADR 0013 N7:
 *
 *  - absence is never "nothing happened": a run that was never captured, one
 *    whose capture is open, one whose capture failed and one whose producer
 *    predates the contract are four different sentences;
 *  - a partial trace is never presented as a complete one: `incomplete` is its
 *    own state, and completeness is shown beside every readable run;
 *  - nothing here infers, fabricates or "improves" a fact the service did not
 *    state. Every value below is either read verbatim from the response or is
 *    a closed enum mapped to a sentence; a value this build cannot read renders
 *    as unreadable rather than as a guess.
 *
 * Pure and hermetic on purpose: it is the part of the feature the fast PR gate
 * (`src/lib`) proves on every push, and it holds every decision the component
 * would otherwise make inline — which state a response is, whether that state
 * can still change, what each closed code means.
 */

export type PreviewRunSummaryResponse =
  components["schemas"]["PreviewRunSummary"];
export type PreviewRunState = components["schemas"]["PreviewRunState"];
export type PreviewRunCaptureState =
  components["schemas"]["PreviewRunCaptureState"];
export type PreviewRunContentUnavailableReason =
  components["schemas"]["PreviewRunContentUnavailableReason"];
export type PreviewTraceFailureCode =
  components["schemas"]["PreviewTraceFailureCode"];
/**
 * This service's OWN dispatch record for the run, beside upstream's header: the
 * preview-target revision the run was dispatched under, and the dispatched
 * candidate's display revision resolved server-side from the candidate id the
 * correlation row names. The page names a testing version from this and from
 * nothing else — never from a content hash, which an unchanged refreeze lets
 * two revisions share (#1098 review, findings 1 and 2).
 */
export type PreviewRunDispatch = components["schemas"]["PreviewRunDispatch"];

/**
 * The fixed disclaimer, rendered on EVERY state of the run page — including
 * the ones that never see a response. Brain's contract pins the same literal
 * (its rule R3: "`synthetic` is literally `true` and `disclaimer` is the fixed
 * literal"), so the page does not depend on an envelope arriving to say it.
 */
export const SYNTHETIC_PREVIEW_DISCLAIMER =
  "Synthetic preview — not a clinical document";

/** The producer contract version this build reads. */
export const SUPPORTED_TRACE_SCHEMA_VERSION = "clinical-preview-trace.v1";

/**
 * Brain's five envelope statuses, transcribed from the approved design §7.1
 * and the producer contract's `status` enum. A CLOSED list on purpose: a
 * status outside it is not a sixth state to invent a sentence for, it is an
 * envelope this build cannot read.
 */
export const TRACE_STATUSES = [
  "waiting",
  "live",
  "completed",
  "incomplete",
  "unavailable",
] as const;
export type TraceStatus = (typeof TRACE_STATUSES)[number];

/**
 * The producer contract's omission and missing-range reasons. Only
 * `restricted_technical` leaves a trace complete — it is a policy outcome, not
 * a shortfall — which is why `OMISSION_REASON_LABELS` says so in words.
 */
export const OMISSION_REASONS = [
  "restricted_technical",
  "size_cap_exceeded",
  "capture_unavailable",
  "unsupported_producer_version",
] as const;
export type OmissionReason = (typeof OMISSION_REASONS)[number];

/** The literal a producer records on an identity axis it could not attest. */
export const UNATTESTED_IDENTITY = "unknown";

export type MissingRange = {
  fromSequence: number | null;
  toSequence: number | null;
  reason: OmissionReason | null;
};

export type TraceOmission = {
  eventRef: string | null;
  field: string | null;
  reason: OmissionReason | null;
};

export type TraceCompleteness = {
  completeThroughSequence: number | null;
  missingRanges: readonly MissingRange[];
  omissions: readonly TraceOmission[];
};

/**
 * Runtime identity — evidence about the run that served the conversation,
 * displayed read-only. Each axis is the producer's own string, or `null` when
 * the envelope did not carry it at all; `UNATTESTED_IDENTITY` is a value the
 * producer chose and is kept distinct from absence.
 */
export type RunIdentity = {
  candidateContentHash: string | null;
  basePackSha: string | null;
  servingBrainCommit: string | null;
  servingImageDigest: string | null;
  model: string | null;
  modelDeployment: string | null;
};

export const IDENTITY_AXES = [
  "candidateContentHash",
  "basePackSha",
  "servingBrainCommit",
  "servingImageDigest",
  "model",
  "modelDeployment",
] as const satisfies readonly (keyof RunIdentity)[];

export const IDENTITY_LABELS = {
  candidateContentHash: "Candidate content hash",
  basePackSha: "Base pack",
  servingBrainCommit: "Serving Brain commit",
  servingImageDigest: "Serving image digest",
  model: "Model",
  modelDeployment: "Model deployment",
} as const satisfies Record<keyof RunIdentity, string>;

/** The envelope without its events, as this build reads it. */
export type TraceSummary = {
  status: TraceStatus;
  disclaimer: string | null;
  updatedAt: string | null;
  lastSequence: number | null;
  /** Added by the storing service beside the envelope; null when absent. */
  eventCount: number | null;
  completeness: TraceCompleteness;
  identity: RunIdentity;
};

export type TraceSummaryReading =
  /** `summary` was null — the contract's "nothing to project". */
  | { kind: "absent" }
  /**
   * `summary` was present but is not an envelope this build can read: its
   * status is outside the closed list, or its synthetic marker is not the
   * literal `true`. Presented as unreadable, never partially rendered — a
   * document that does not say it is synthetic is not shown as a preview.
   */
  | { kind: "unreadable"; reason: "status" | "synthetic_marker" }
  | { kind: "readable"; summary: TraceSummary };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function stringOrNull(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

/**
 * An integer, or null. Brain's own validator accepts an integral number "in
 * either spelling" (its R25), so `2.0` reads as `2`; anything non-finite or
 * fractional is not a sequence number and reads as absent.
 */
function integerOrNull(value: unknown): number | null {
  return typeof value === "number" && Number.isInteger(value) ? value : null;
}

function isOmissionReason(value: unknown): value is OmissionReason {
  return (
    typeof value === "string" &&
    (OMISSION_REASONS as readonly string[]).includes(value)
  );
}

function isTraceStatus(value: unknown): value is TraceStatus {
  return (
    typeof value === "string" &&
    (TRACE_STATUSES as readonly string[]).includes(value)
  );
}

function readMissingRanges(value: unknown): MissingRange[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter(isRecord).map((entry) => ({
    fromSequence: integerOrNull(entry["fromSequence"]),
    toSequence: integerOrNull(entry["toSequence"]),
    reason: isOmissionReason(entry["reason"]) ? entry["reason"] : null,
  }));
}

function readOmissions(value: unknown): TraceOmission[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter(isRecord).map((entry) => ({
    eventRef: stringOrNull(entry["eventRef"]),
    field: stringOrNull(entry["field"]),
    reason: isOmissionReason(entry["reason"]) ? entry["reason"] : null,
  }));
}

function readIdentity(value: unknown): RunIdentity {
  const source = isRecord(value) ? value : {};
  return {
    candidateContentHash: stringOrNull(source["candidateContentHash"]),
    basePackSha: stringOrNull(source["basePackSha"]),
    servingBrainCommit: stringOrNull(source["servingBrainCommit"]),
    servingImageDigest: stringOrNull(source["servingImageDigest"]),
    model: stringOrNull(source["model"]),
    modelDeployment: stringOrNull(source["modelDeployment"]),
  };
}

/**
 * Reads the proxied envelope into `TraceSummary`.
 *
 * Tolerant about MEMBERS and strict about MEANING: a member the envelope does
 * not carry, or carries in a shape this build does not understand, reads as
 * `null` and renders as "not reported" — but a status outside the closed list
 * or a synthetic marker that is not `true` makes the whole envelope
 * unreadable. The first kind of gap is the service declining to say something
 * (the route tests upstream ship an `identity` with two of six axes); the
 * second is a document this build has no business presenting as a preview.
 */
export function readTraceSummary(
  summary: PreviewRunSummaryResponse["summary"] | undefined,
): TraceSummaryReading {
  if (summary === null || summary === undefined || !isRecord(summary)) {
    return { kind: "absent" };
  }
  const status = summary["status"];
  if (!isTraceStatus(status)) {
    return { kind: "unreadable", reason: "status" };
  }
  if (summary["synthetic"] !== true) {
    return { kind: "unreadable", reason: "synthetic_marker" };
  }
  const completeness = isRecord(summary["completeness"])
    ? summary["completeness"]
    : {};
  return {
    kind: "readable",
    summary: {
      status,
      disclaimer: stringOrNull(summary["disclaimer"]),
      updatedAt: stringOrNull(summary["updatedAt"]),
      lastSequence: integerOrNull(summary["lastSequence"]),
      eventCount: integerOrNull(summary["eventCount"]),
      completeness: {
        completeThroughSequence: integerOrNull(
          completeness["completeThroughSequence"],
        ),
        missingRanges: readMissingRanges(completeness["missingRanges"]),
        omissions: readOmissions(completeness["omissions"]),
      },
      identity: readIdentity(summary["identity"]),
    },
  };
}

/**
 * Why a run that RESOLVED cannot be shown as a readable trace. Each member is
 * a distinct fact the response stated; none of them is "the run does not
 * exist", which is the server's generic 404 and never a state of this page.
 */
export type UnavailableReason =
  /** `captureState: failed` — capture ran and recorded a failure code. */
  | { kind: "capture_failed"; failureCode: PreviewTraceFailureCode | null }
  /** `captureState: unsupported` — the serving Brain predates the contract. */
  | { kind: "producer_unsupported"; failureCode: PreviewTraceFailureCode | null }
  /** `contentUnavailableReason: contract_refused` — stored, but the recorded target can no longer read it. */
  | { kind: "stored_unreadable" }
  /** `contentUnavailableReason: unsupported_version` — stored under a producer contract this build does not read. */
  | { kind: "version_unsupported"; schemaVersion: string | null }
  /** Brain's own `status: unavailable`, carrying the trace-scoped omission's reason. */
  | { kind: "trace_unavailable"; reason: OmissionReason | null }
  /**
   * The header says content is available but the envelope beside it is
   * absent or unreadable — a contract disagreement this build fails closed
   * on rather than renders as an empty run.
   */
  | { kind: "summary_unreadable"; reason: "absent" | "status" | "synthetic_marker" }
  /**
   * A header this build cannot place: content unavailable for no stated
   * reason on a capture state that says content exists. Fail closed.
   */
  | { kind: "inconsistent" };

/**
 * What the run page renders, derived from ONE response.
 *
 * `waiting` carries WHY it is waiting, because the three reasons are the three
 * absences ADR 0013 N7 forbids collapsing: the target has not opened a capture
 * (`absent`), the capture is open with nothing in it yet (`pending`), or Brain
 * admitted the run and has recorded nothing (`admitted`, the envelope's own
 * `waiting`).
 */
export type PreviewRunView = {
  run: PreviewRunState;
  summary: TraceSummary | null;
} & (
  | { kind: "waiting"; because: "not_captured" | "capture_pending" | "admitted" }
  | { kind: "live" }
  | { kind: "completed" }
  | { kind: "incomplete" }
  | { kind: "unavailable"; reason: UnavailableReason }
);

export type PreviewRunViewKind = PreviewRunView["kind"];

/** Folds the run header and the envelope into the page's one view. */
export function derivePreviewRunView(
  response: PreviewRunSummaryResponse,
): PreviewRunView {
  const { run } = response;
  const reading = readTraceSummary(response.summary);
  const summary = reading.kind === "readable" ? reading.summary : null;

  if (!run.contentAvailable) {
    const reason = run.contentUnavailableReason ?? null;
    if (reason === "unsupported_version") {
      return {
        kind: "unavailable",
        run,
        summary,
        reason: {
          kind: "version_unsupported",
          schemaVersion: run.schemaVersion ?? null,
        },
      };
    }
    if (reason === "contract_refused") {
      return {
        kind: "unavailable",
        run,
        summary,
        reason: { kind: "stored_unreadable" },
      };
    }
    // `not_captured`, or no reason at all: the capture state is the fact.
    switch (run.captureState) {
      case "absent":
        return { kind: "waiting", because: "not_captured", run, summary };
      case "pending":
        return { kind: "waiting", because: "capture_pending", run, summary };
      case "failed":
        return {
          kind: "unavailable",
          run,
          summary,
          reason: {
            kind: "capture_failed",
            failureCode: run.failureCode ?? null,
          },
        };
      case "unsupported":
        return {
          kind: "unavailable",
          run,
          summary,
          reason: {
            kind: "producer_unsupported",
            failureCode: run.failureCode ?? null,
          },
        };
      case "captured":
      case "incomplete":
        // A stored snapshot with content withheld for no stated reason is not
        // a shape the service publishes; it is not rendered as an empty run.
        return {
          kind: "unavailable",
          run,
          summary,
          reason: { kind: "inconsistent" },
        };
    }
  }

  if (reading.kind !== "readable") {
    return {
      kind: "unavailable",
      run,
      summary: null,
      reason: {
        kind: "summary_unreadable",
        reason: reading.kind === "absent" ? "absent" : reading.reason,
      },
    };
  }

  switch (reading.summary.status) {
    case "waiting":
      return { kind: "waiting", because: "admitted", run, summary };
    case "live":
      return { kind: "live", run, summary };
    case "completed":
      return { kind: "completed", run, summary };
    case "incomplete":
      return { kind: "incomplete", run, summary };
    case "unavailable":
      return {
        kind: "unavailable",
        run,
        summary,
        reason: {
          kind: "trace_unavailable",
          reason:
            reading.summary.completeness.omissions.find(
              (omission) => omission.eventRef === null,
            )?.reason ?? null,
        },
      };
  }
}

/**
 * Whether a view can still change on its own, and therefore whether the page
 * keeps polling the compact envelope.
 *
 * `waiting`, `live` and `incomplete` can: the first two by definition, and
 * `incomplete` because nothing inside the producer's envelope says a run
 * ended (its R7 makes `live` and `completed` the one deliberate pair) — a
 * conversation that lost one range keeps producing turns. `completed` and
 * every `unavailable` arm are terminal for this page: the next poll would ask
 * the same question and get the same answer, and a clinician who wants to ask
 * again has the refresh control.
 */
export function canStillChange(view: PreviewRunView): boolean {
  switch (view.kind) {
    case "waiting":
    case "live":
    case "incomplete":
      return true;
    case "completed":
    case "unavailable":
      return false;
  }
}

/** How often the page re-reads a run that can still change. */
export const RUN_POLL_INTERVAL_MS = 5_000;

export const VIEW_LABELS = {
  waiting: "Waiting",
  live: "Live",
  completed: "Completed",
  incomplete: "Incomplete",
  unavailable: "Unavailable",
} as const satisfies Record<PreviewRunViewKind, string>;

export const VIEW_TONES = {
  waiting: "outline",
  live: "success",
  completed: "neutral",
  incomplete: "warning",
  unavailable: "destructive",
} as const satisfies Record<
  PreviewRunViewKind,
  "outline" | "success" | "neutral" | "warning" | "destructive"
>;

export const CAPTURE_STATE_LABELS = {
  absent: "Not captured",
  pending: "Capture open, nothing recorded yet",
  captured: "Captured",
  incomplete: "Captured with gaps",
  failed: "Capture failed",
  unsupported: "Not supported by the serving Brain",
} as const satisfies Record<PreviewRunCaptureState, string>;

export const CONTENT_UNAVAILABLE_LABELS = {
  not_captured: "no trace was captured for this run",
  contract_refused:
    "the stored trace can no longer be read by the recorded preview target",
  unsupported_version:
    "the stored trace uses a producer contract version this Editor build does not read",
} as const satisfies Record<PreviewRunContentUnavailableReason, string>;

export const FAILURE_CODE_LABELS = {
  capture_unavailable: "capture was unavailable when the turn ran",
  contract_refused: "the recorded target refused the trace contract",
  unsupported_producer_version:
    "the serving Brain produced a trace version the target does not accept",
  size_cap_exceeded: "the trace exceeded the size cap",
} as const satisfies Record<PreviewTraceFailureCode, string>;

export const OMISSION_REASON_LABELS = {
  restricted_technical: "restricted technical content, withheld by policy",
  size_cap_exceeded: "content over the size cap, withheld whole",
  capture_unavailable: "capture failed or never ran",
  unsupported_producer_version:
    "the serving Brain predates this trace version",
} as const satisfies Record<OmissionReason, string>;

/** One sentence per view, stating the fact and nothing beyond it. */
export function describeView(view: PreviewRunView): string {
  switch (view.kind) {
    case "waiting":
      return describeWaiting(view.because);
    case "live":
      return "Turns are being recorded. This page re-reads the run's status while it can still change.";
    case "completed":
      return "The run ended and its trace is complete through the last recorded turn.";
    case "incomplete":
      return "The trace is readable but partial: something was not captured, or was withheld, and is listed under completeness. Nothing missing is presented as if it did not happen.";
    case "unavailable":
      return describeUnavailable(view.reason);
  }
}

function describeWaiting(
  because: Extract<PreviewRunView, { kind: "waiting" }>["because"],
): string {
  switch (because) {
    case "not_captured":
      return "The recorded preview target has not opened a trace capture for this run yet. It does so when the first synthetic turn is sent.";
    case "capture_pending":
      return "A trace capture is open for this run, but nothing has been recorded yet.";
    case "admitted":
      return "Liv admitted this candidate-bound preview and has not recorded a turn yet.";
  }
}

function describeUnavailable(reason: UnavailableReason): string {
  switch (reason.kind) {
    case "capture_failed":
      return `Trace capture failed for this run${
        reason.failureCode ? `: ${FAILURE_CODE_LABELS[reason.failureCode]}` : ""
      }. The preview conversation itself was not affected.`;
    case "producer_unsupported":
      return `The Brain that served this run predates the trace contract${
        reason.failureCode ? ` (${FAILURE_CODE_LABELS[reason.failureCode]})` : ""
      }, so no trace could be captured. This is not a statement about the conversation.`;
    case "stored_unreadable":
      return `A trace was stored, but ${CONTENT_UNAVAILABLE_LABELS.contract_refused}.`;
    case "version_unsupported":
      return `A trace was stored, but ${CONTENT_UNAVAILABLE_LABELS.unsupported_version}${
        reason.schemaVersion ? ` (${reason.schemaVersion})` : ""
      }. An operator can roll a build that reads it; this one presents nothing rather than a guess.`;
    case "trace_unavailable":
      return `The producer reported this trace as unavailable${
        reason.reason ? `: ${OMISSION_REASON_LABELS[reason.reason]}` : ""
      }.`;
    case "summary_unreadable":
      return "The Editor Service reported readable content for this run, but the trace envelope it relayed is not one this build can read. Nothing is shown rather than a partial reading.";
    case "inconsistent":
      return "The run's capture state and its content availability disagree. Nothing is shown rather than a reading this build cannot vouch for.";
  }
}

/**
 * The "last update" instant a clinician should trust, in order of authority:
 * the producer's own `updatedAt`, then when the storing service accepted the
 * snapshot, then when it last wrote the run's row. Null when none was stated.
 */
export function lastUpdateOf(view: PreviewRunView): string | null {
  return (
    view.summary?.updatedAt ??
    view.run.capturedAt ??
    view.run.observedAt ??
    null
  );
}

/**
 * The completeness line — "complete through N of M" plus what is declared
 * missing — stated only from the envelope's own numbers.
 */
export type CompletenessReading = {
  completeThroughSequence: number | null;
  lastSequence: number | null;
  missingRangeCount: number;
  /** Omissions that make the trace incomplete (everything but restricted content). */
  shortfallOmissionCount: number;
  /** Omissions that are policy outcomes, listed separately so they never read as loss. */
  restrictedOmissionCount: number;
};

export function completenessOf(summary: TraceSummary): CompletenessReading {
  const restricted = summary.completeness.omissions.filter(
    (omission) => omission.reason === "restricted_technical",
  ).length;
  return {
    completeThroughSequence: summary.completeness.completeThroughSequence,
    lastSequence: summary.lastSequence,
    missingRangeCount: summary.completeness.missingRanges.length,
    shortfallOmissionCount: summary.completeness.omissions.length - restricted,
    restrictedOmissionCount: restricted,
  };
}

/**
 * The candidate label the preview dialog already uses — `Test version N` —
 * from the revision THIS SERVICE resolved for the dispatched candidate, or
 * null when it resolved none. Deliberately no fallback label: a version the
 * server could not prove is not named at all, and the content hash the
 * producer attested is shown beside it either way.
 */
export function candidateDisplayVersion(
  revision: PreviewRunDispatch["candidateRevision"] | undefined,
): string | null {
  return revision ? `Test version ${revision.number}` : null;
}

/**
 * The run's target revision, stated against the Project's CURRENT one when
 * that is known: a Project retargeted since the dispatch still shows which
 * revision actually served this run, and says that the Project moved.
 */
export function describeTargetRevision(
  dispatched: number,
  current: number | null | undefined,
): string {
  if (current === null || current === undefined || current === dispatched) {
    return `Revision ${dispatched}`;
  }
  return `Revision ${dispatched} at dispatch; the project now points at revision ${current}`;
}

/**
 * The run page's address: the Project id and the opaque locator, nothing
 * else. Approved design §10.1 — "The route contains only an opaque Editor
 * locator. It contains no patient, candidate or Liv/Brain session identifier."
 * Built in one place so the dialog's link and the route table cannot drift.
 */
export function previewRunPath(projectId: string, previewRunRef: string): string {
  return `/projects/${encodeURIComponent(projectId)}/preview-runs/${encodeURIComponent(previewRunRef)}`;
}

/** Whether an identity axis carries an attested value rather than the producer's "unknown". */
export function isAttested(value: string | null): value is string {
  return value !== null && value !== UNATTESTED_IDENTITY;
}
